// Hook personalizado para manejar categorías y subcategorías
// Proporciona estado y funciones para trabajar con categorías

import { useState, useEffect, useCallback } from 'react'
import {
  getCategories,
  getSubcategories,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory
} from '../services/categories.service'

/**
 * Hook para obtener y manejar categorías
 * @param {boolean} includeInactive - Si incluir categorías inactivas
 * @returns {Object} { categories, loading, error, refresh }
 */
export const useCategories = (includeInactive = false) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getCategories(includeInactive)
      setCategories(data)
    } catch (err) {
      console.error('Error cargando categorías:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  return {
    categories,
    loading,
    error,
    refresh: fetchCategories
  }
}

/**
 * Hook para obtener y manejar subcategorías
 * @param {string} categoryId - Filtrar por categoría (opcional)
 * @param {boolean} includeInactive - Si incluir subcategorías inactivas
 * @returns {Object} { subcategories, loading, error, refresh }
 */
export const useSubcategories = (categoryId = null, includeInactive = false) => {
  const [subcategories, setSubcategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSubcategories = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getSubcategories(categoryId, includeInactive)
      setSubcategories(data)
    } catch (err) {
      console.error('Error cargando subcategorías:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [categoryId, includeInactive])

  useEffect(() => {
    fetchSubcategories()
  }, [fetchSubcategories])

  return {
    subcategories,
    loading,
    error,
    refresh: fetchSubcategories
  }
}

/**
 * Hook para operaciones CRUD de categorías
 * @returns {Object} Funciones para crear, actualizar y eliminar categorías
 */
export const useCategoryActions = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const create = async (categoryData) => {
    try {
      setLoading(true)
      setError(null)
      const id = await createCategory(categoryData)
      return id
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const update = async (id, categoryData) => {
    try {
      setLoading(true)
      setError(null)
      await updateCategory(id, categoryData)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const remove = async (id) => {
    try {
      setLoading(true)
      setError(null)
      await deleteCategory(id)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    update,
    remove,
    loading,
    error
  }
}

/**
 * Hook para operaciones CRUD de subcategorías
 * @returns {Object} Funciones para crear, actualizar y eliminar subcategorías
 */
export const useSubcategoryActions = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const create = async (subcategoryData) => {
    try {
      setLoading(true)
      setError(null)
      const id = await createSubcategory(subcategoryData)
      return id
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const update = async (id, subcategoryData) => {
    try {
      setLoading(true)
      setError(null)
      await updateSubcategory(id, subcategoryData)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const remove = async (id) => {
    try {
      setLoading(true)
      setError(null)
      await deleteSubcategory(id)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    update,
    remove,
    loading,
    error
  }
}

